// components/patient/Register.jsx
import React, { useState } from 'react';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { DB } from '../../utils/db';
import { authService } from '../../services/authService';
import { US_STATES } from '../../utils/constants';

export function Register({ onNavigate, onRegister }) {
  const { brand } = useBrand();
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dob: '',
    password: '',
    confirmPassword: '',
    street: '',
    apt: '',
    city: '',
    state: '',
    zip: '',
    hasInsurance: false,
    insuranceProvider: '',
    memberId: '',
    groupNumber: ''
  });
  const [errors, setErrors] = useState({});
  
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };
  
  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.dob) newErrors.dob = 'Date of birth is required';
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.street.trim()) newErrors.street = 'Street address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.zip.trim()) {
      newErrors.zip = 'ZIP code is required';
    } else if (!/^\d{5}$/.test(formData.zip)) {
      newErrors.zip = 'ZIP code must be 5 digits';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleNext = async () => {
    if (step === 1) {
      if (!validateStep1()) return;
      
      try {
        const emailExists = await authService.checkEmailExists(formData.email);
        if (emailExists) {
          setErrors({ email: 'Email already registered' });
          return;
        }
      } catch {
        const existingUserId = await DB.get(`email:${formData.email}`);
        if (existingUserId) {
          setErrors({ email: 'Email already registered' });
          return;
        }
      }
      
      setStep(2);
    } else if (step === 2) {
      if (!validateStep2()) return;
      setStep(3);
    }
  };
  
  const handleSubmit = async () => {
    try {
      const result = await authService.register({
        email: formData.email.trim(),
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        dob: formData.dob,
        isB2B: brand.isB2B,
        address: {
          street: formData.street.trim(),
          apt: formData.apt.trim(),
          city: formData.city.trim(),
          state: formData.state,
          zip: formData.zip.trim()
        }
      });

      const apiUser = result.user;
      await DB.set(`user:${apiUser.id}`, apiUser);
      await DB.set(`email:${formData.email.trim().toLowerCase()}`, apiUser.id);
      await DB.set('d2p_session', apiUser.id);
      toast.addToast('Account created successfully!');
      onRegister(apiUser);
    } catch (err) {
      console.warn('API registration failed, falling back to localStorage:', err.message);
      const userId = `user_${Date.now()}`;
      const user = {
        id: userId,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        dob: formData.dob,
        password: formData.password,
        address: {
          street: formData.street.trim(),
          apt: formData.apt.trim(),
          city: formData.city.trim(),
          state: formData.state,
          zip: formData.zip.trim()
        },
        insurance: formData.hasInsurance ? {
          provider: formData.insuranceProvider.trim(),
          memberId: formData.memberId.trim(),
          groupNumber: formData.groupNumber.trim()
        } : null,
        createdAt: new Date().toISOString(),
        isB2B: brand.isB2B
      };
      await DB.set(`user:${userId}`, user);
      await DB.set(`email:${formData.email.trim().toLowerCase()}`, userId);
      await DB.set('d2p_session', userId);
      toast.addToast('Account created successfully!');
      onRegister(user);
    }
  };
  
  return (
    <div style={{
      minHeight: '100vh',
      background: '#F9FAFB',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        {/* Progress Steps */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 16,
          marginBottom: 40
        }}>
          {[1, 2, 3].map(s => (
            <React.Fragment key={s}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: step >= s ? brand.c.accent : '#E5E7EB',
                color: step >= s ? 'white' : '#9CA3AF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                transition: 'all 0.3s'
              }}>
                {s}
              </div>
              {s < 3 && (
                <div style={{
                  width: 60,
                  height: 2,
                  background: step > s ? brand.c.accent : '#E5E7EB',
                  transition: 'all 0.3s'
                }} />
              )}
            </React.Fragment>
          ))}
        </div>
        
        <Card>
          {step === 1 && (
            <>
              <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
                Create Your Account
              </h2>
              <p style={{ color: '#6B7280', marginBottom: 32 }}>
                Step 1 of 3: Personal Information
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                <Input
                  label="First Name"
                  value={formData.firstName}
                  onChange={e => updateField('firstName', e.target.value)}
                  error={errors.firstName}
                  required
                />
                <Input
                  label="Last Name"
                  value={formData.lastName}
                  onChange={e => updateField('lastName', e.target.value)}
                  error={errors.lastName}
                  required
                />
              </div>
              
              <Input
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={e => updateField('email', e.target.value)}
                error={errors.email}
                required
              />
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                <Input
                  label="Phone Number"
                  type="tel"
                  value={formData.phone}
                  onChange={e => updateField('phone', e.target.value)}
                  error={errors.phone}
                  placeholder="(555) 123-4567"
                  required
                />
                <Input
                  label="Date of Birth"
                  type="date"
                  value={formData.dob}
                  onChange={e => updateField('dob', e.target.value)}
                  error={errors.dob}
                  required
                />
              </div>
              
              <Input
                label="Password"
                type="password"
                value={formData.password}
                onChange={e => updateField('password', e.target.value)}
                error={errors.password}
                help="Must be at least 8 characters"
                required
              />
              
              <Input
                label="Confirm Password"
                type="password"
                value={formData.confirmPassword}
                onChange={e => updateField('confirmPassword', e.target.value)}
                error={errors.confirmPassword}
                required
              />
              
              <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                <Button onClick={handleNext} fullWidth>
                  Continue
                </Button>
                <Button variant="ghost" onClick={() => onNavigate('landing')}>
                  Cancel
                </Button>
              </div>
            </>
          )}
          
          {step === 2 && (
            <>
              <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
                Shipping Address
              </h2>
              <p style={{ color: '#6B7280', marginBottom: 32 }}>
                Step 2 of 3: Where should we deliver your medications?
              </p>
              
              <Input
                label="Street Address"
                value={formData.street}
                onChange={e => updateField('street', e.target.value)}
                error={errors.street}
                required
              />
              
              <Input
                label="Apartment / Suite (Optional)"
                value={formData.apt}
                onChange={e => updateField('apt', e.target.value)}
              />
              
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 16 }}>
                <Input
                  label="City"
                  value={formData.city}
                  onChange={e => updateField('city', e.target.value)}
                  error={errors.city}
                  required
                />
                <Input
                  label="State"
                  type="select"
                  value={formData.state}
                  onChange={e => updateField('state', e.target.value)}
                  error={errors.state}
                  options={US_STATES}
                  required
                />
                <Input
                  label="ZIP Code"
                  value={formData.zip}
                  onChange={e => updateField('zip', e.target.value)}
                  error={errors.zip}
                  placeholder="12345"
                  required
                />
              </div>
              
              <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                <Button onClick={() => setStep(1)} variant="secondary">
                  Back
                </Button>
                <Button onClick={handleNext} fullWidth>
                  Continue
                </Button>
              </div>
            </>
          )}
          
          {step === 3 && (
            <>
              <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
                Insurance Information
              </h2>
              <p style={{ color: '#6B7280', marginBottom: 32 }}>
                Step 3 of 3: Optional insurance details (can be added later)
              </p>
              
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 24,
                cursor: 'pointer',
                padding: 16,
                background: '#F9FAFB',
                borderRadius: 8,
                border: '2px solid #E5E7EB'
              }}>
                <input
                  type="checkbox"
                  checked={formData.hasInsurance}
                  onChange={e => updateField('hasInsurance', e.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                <span style={{ fontWeight: 600 }}>
                  I have insurance coverage
                </span>
              </label>
              
              {formData.hasInsurance && (
                <>
                  <Input
                    label="Insurance Provider"
                    value={formData.insuranceProvider}
                    onChange={e => updateField('insuranceProvider', e.target.value)}
                    placeholder="e.g., Blue Cross Blue Shield"
                  />
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                    <Input
                      label="Member ID"
                      value={formData.memberId}
                      onChange={e => updateField('memberId', e.target.value)}
                    />
                    <Input
                      label="Group Number"
                      value={formData.groupNumber}
                      onChange={e => updateField('groupNumber', e.target.value)}
                    />
                  </div>
                </>
              )}
              
              <div style={{
                background: '#EFF6FF',
                padding: 16,
                borderRadius: 8,
                marginTop: 24,
                marginBottom: 24
              }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'start' }}>
                  <Icon name="alert" size={20} color="#3B82F6" />
                  <div style={{ fontSize: 13, color: '#1E40AF', lineHeight: 1.6 }}>
                    <strong>Note:</strong> Insurance information is optional. You can add it now or later from your profile. 
                    {brand.name} pricing is often lower than insurance copays.
                  </div>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: 12 }}>
                <Button onClick={() => setStep(2)} variant="secondary">
                  Back
                </Button>
                <Button onClick={handleSubmit} fullWidth>
                  Create Account
                </Button>
              </div>
            </>
          )}
          
          <div style={{
            marginTop: 24,
            paddingTop: 24,
            borderTop: '1px solid #E5E7EB',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: 14, color: '#6B7280' }}>
              Already have an account?{' '}
              <button
                onClick={() => onNavigate('login')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: brand.c.accent,
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Sign in
              </button>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
